import { Box, Typography, Tooltip } from '@mui/material';
import { useState, useMemo } from 'react';
import { useIntentsQuery } from '@tymely/services';

import { IntentSuggestion, IntentSuggestionEventProps } from '../types';

export const IntentSuggestionEvent = ({ eventData, isExpanded, tools }: IntentSuggestionEventProps) => {
    const [showToolDescription, setShowToolDescription] = useState<number | null>(null);
    const intents = useIntentsQuery();

    const mergedItems = useMemo(() => {
        const matchedToolNames = new Set<string>();
        const intentToolNames: string[] = [];
        const merged: { intent?: IntentSuggestion; tool?: { name: string; description: string } }[] = [];

        if (eventData?.audit_log?.suggested_intents && tools) {
            eventData.audit_log.suggested_intents.forEach((intent) => {
                const toolName = intents.data?.find(({ id }) => id === intent.intent_id)?.additional_data?.tool?.name;
                if (toolName) intentToolNames.push(toolName);
                const matchedTool = toolName
                    ? tools.find((tool) => tool.name && tool.name.includes(toolName))
                    : undefined;
                if (matchedTool && matchedTool.name) {
                    matchedToolNames.add(matchedTool.name);
                }
                merged.push({ intent, tool: matchedTool });
            });
            tools.forEach((tool) => {
                if (!matchedToolNames.has(tool.name)) {
                    merged.push({ intent: undefined, tool });
                }
            });
        }
        return merged;
    }, [eventData?.audit_log?.suggested_intents, tools, intents.data]);

    if (!isExpanded) {
        return null;
    }

    return (
        <Box>
            {mergedItems.map((entry, idx) => {
                const { intent, tool } = entry;
                const showIdx = idx + 1000;
                const toolName = tool?.name;
                const toolDescription = tool?.description;
                const intentData = intent && intents.data?.find(({ id }) => id === intent.intent_id);
                const handleShowToolDescription = () => {
                    if (showToolDescription === showIdx) {
                        setShowToolDescription(null);
                    } else {
                        setShowToolDescription(showIdx);
                    }
                };
                return (
                    <Box
                        key={idx}
                        sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start', ml: 2 }}
                    >
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {intent && (
                                <Typography variant="body2" color="text.secondary">
                                    Intent ID:{' '}
                                    <Tooltip
                                        title={`${intentData?.name || 'Unknown Intent'} (Score: ${intent.score.toFixed(2)})`}
                                    >
                                        <b>{intent.intent_id}</b>
                                    </Tooltip>
                                </Typography>
                            )}
                            {toolName && (
                                <Typography variant="body2" color="text.secondary">
                                    Tool:{' '}
                                    <b
                                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                        onClick={handleShowToolDescription}
                                    >
                                        {toolName}
                                    </b>
                                </Typography>
                            )}
                        </Box>
                        {showToolDescription === showIdx && toolDescription && (
                            <Box sx={{ pl: 2, borderLeft: '2px solid #ccc', ml: 1, mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {toolDescription}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};
